/**
 * Mapping a register's columns — ProDoc's mapper, on ProGST's screen (X-14 (l), ADR-0045).
 *
 * **This is ADR-0026 condition 3, still**: a person looks at the grid and says which column is
 * which. What changed is whose screen does the looking. ProGST no longer reads a client's grids:
 * ProDoc does, under the client's column map kept in ProDoc, and a file no map fits is refused
 * `mapping_needed` with what a person needs to map it. The grid, the header row, the bindings,
 * the preview of the reading and the save are `<ProDocColumnMapper>`'s, from `@fiveox/prodoc-ui`.
 * What stays ProGST's is the sentence around it and what happens after the save.
 *
 * **It reaches ProDoc through ProGST's service** (ADR-0045 decision 4, `api/prodoc_mapper.py`):
 * the package's client is pointed at a bridge under the client, so every call it makes lands at
 * ProGST first, under the person's ProGST permission, and goes on to ProDoc as ProGST — the map a
 * person saves is ProGST's own, and is offered on ProGST's next parse of the client's files.
 *
 * **And for a file already read through a map (D-43)** the mapper opens on that map, and Save
 * adds a version to it: the binding a person got wrong is the one they correct. Found on UAT,
 * 2026-09-30, when the layout was still ProGST's: a rate column bound by hand held every row, and
 * no screen offered the columns again.
 */
import {
  type ColumnMap,
  type MapBinding,
  type MapPreview,
  type MappingNeeded,
  type ParseProfile,
  ProDocColumnMapper,
  createProDocClient,
  mappingOf,
} from '@fiveox/prodoc-ui'
import { Alert, Button, Card } from '@fiveox/ui'
import { useEffect, useMemo, useState } from 'react'

import { CLIENT, useEvaluation } from '../lib/access'
import {
  ApiError,
  type ColumnMapRecord,
  type DocumentMapping,
  type DocumentRecord,
  columnMaps,
  documents,
  exportFacts,
  getOrgId,
} from '../lib/api'
import { SERVICE_API } from '../lib/config'
import { invoiceTypeOf } from '../lib/invoiceType'
import { session } from '../lib/session'

/**
 * What ProGST does with a field beyond reading it — labels only, keyed by the fields a register
 * may bind (the service's `bindable_fields`; `tests/seam-vocabularies.test.ts` holds it TOTAL).
 *
 * Handed to the mapper as its `fieldLabels` (ProDoc's W38 follow-up), so each field is named in
 * ProGST's words where the person binds it. Several of these say what binding the field DOES to
 * the return — a cancelled bill counted in Table 13, a platform's sale sent to Table 14, the
 * amounts read as rupees — and a person binding a till's export has to be told that before
 * saving, on the field itself.
 */
const FIELD_LABELS: Record<string, string> = {
  invoice_no: 'Invoice no',
  invoice_date: 'Invoice date',
  buyer_name: 'Buyer',
  gstin_buyer: 'Buyer GSTIN',
  supplier_name: 'Supplier',
  gstin_supplier: 'Supplier GSTIN',
  item: 'Item',
  hsn: 'HSN',
  qty: 'Qty',
  uqc: 'UQC',
  gst_rate: 'GST rate',
  taxable: 'Taxable',
  cgst: 'CGST',
  sgst: 'SGST',
  igst: 'IGST',
  cess: 'Cess',
  // W3-12 (1): what a till report states beside the money.
  place_of_supply: 'Place of supply',
  invoice_value: 'Invoice value',
  is_memo: 'Credit memo — the line is a RETURN',
  cgst_rate: 'CGST rate (half)',
  sgst_rate: 'SGST/UTGST rate (half)',
  igst_rate: 'IGST rate',
  // W3-12 (2): `Credit Note` / `Debit Note` / `Sales` — types each line.
  voucher_type: 'Voucher type (credit / debit note)',
  // W3-17 (2): one column stating the whole tax, split into heads by the place of supply.
  gst_amount: 'GST amount (one column for all the tax)',
  // W3-17 (8b), D-94: the trial-balance ledger each line was posted to.
  ledger: 'Ledger (code or name, as the trial balance has it)',
  // W3-18 (5), D-105 and D-88: the recipient pays the tax; and goods, services or an import of
  // services.
  reverse_charge: 'Reverse charge (Y when the recipient pays the tax)',
  supply_kind: 'Supply type (goods, services, import of services)',
  // W3-19 (2), D-124: `Success` / `Cancelled`, as a till exports each bill.
  doc_status: 'Bill status (a cancelled bill is counted in Table 13, never sold)',
  // W3-19 (3), D-125: how the bill was paid, and where the order came from; a platform in
  // either sends the sale to Table 14.
  payment_channel: 'Payment type (UPI, cash, or a platform paid through)',
  order_channel: 'Order source (a platform, e.g. Zomato, on a delivery)',
  // W3-19 (6), D-128: Inter-State / Intra-State, where the register states it.
  inter_state: 'Inter- or intra-state (as the register states it)',
  // C-04 (FR-E19): a purchase row whose tax the client pays under reverse charge.
  import_of_services: 'Import of services (yes/no)',
  // FR-E11 (B-04): an advances register's kind column — advance received / adjusted.
  advance_kind: 'Advance kind (received / adjusted)',
  // FR-E9 (B-02): an export invoice's shipping bill, as GSTN's EXP sheet heads it.
  shipping_bill_no: 'Shipping bill number',
  shipping_bill_date: 'Shipping bill date',
  port_code: 'Port code',
  // D-143: the invoice's currency; a sale not in rupees holds until it can be converted.
  currency: 'Currency',
  // W3-16 (c), D-148: regular, SEZ, deemed export or export, as the register states it; and the
  // value in the invoice's own currency, shown beside the rupees and never added as rupees.
  // D-149 (G18 = A): binding it says the amount columns are rupees, and a sale in another
  // currency then books at them, so the label says so before the person binds it.
  invoice_type: 'Invoice type (regular, SEZ, deemed export, export)',
  fc_value: 'Value in the invoice’s currency (shown, never added as rupees; amounts then read as rupees)',
}

/**
 * A field as it reads inside a sentence — `place of supply`, and `IGST` kept as it is — the
 * service's `_words` (D-152), from the label before what it adds in brackets.
 */
export function fieldWords(name: string): string {
  // FIELD_LABELS is total over the fields a register may bind (`seam-vocabularies.test.ts`).
  const label = (FIELD_LABELS[name] ?? name).split(/ \(| —/)[0]!.trim()
  const first = label.split(' ')[0] ?? ''
  return first === first.toUpperCase() ? label : label.charAt(0).toLowerCase() + label.slice(1)
}

/**
 * Where the package's client is pointed: ProGST's bridge for this client. The package appends
 * `/api/v1/<its path>`, so its five calls land on `…/prodoc-mapper/api/v1/{profiles,
 * column-maps, column-maps/{id}/versions, column-maps/{id}, column-maps/preview, jobs/{id}}` —
 * routes no line of ProGST's own calls (`tests/seam-paths.test.ts` says how they are counted).
 */
export function mapperBase(clientId: string): string {
  return `${SERVICE_API}/api/v1/clients/${clientId}/prodoc-mapper`
}

/**
 * ProDoc's `mapping` as the mapper takes it — the package's own reading of it (`mappingOf`), so
 * a refusal that carried less opens on empty choices rather than throwing, and its W38 facts
 * (`misfits`, `unproven_details`) come across typed. Nothing is guessed here.
 */
const NOTHING: MappingNeeded = { sheets: [], candidates: [], suggestion: null }

/** A binding as a person reads it: the heading it names, or the column's letter. */
function columnWords(binding: MapBinding | undefined): string | null {
  if (typeof binding !== 'string' || !binding) return null
  return binding.startsWith('header:') ? binding.slice('header:'.length) : `column ${binding}`
}

/**
 * D-152: why each saved map was not reused on this file — ProDoc's `misfits` of the two kinds a
 * person can act on (the sheet names fields the map does not read), named by the client's map,
 * in the sentence ProGST's own reuse said it in.
 */
export function notReused(
  mapping: MappingNeeded,
  maps: readonly ColumnMapRecord[],
): { id: string; name: string; reason: string }[] {
  const named = new Map(maps.map((m) => [m.id, m.name]))
  return (mapping.misfits ?? []).flatMap((misfit) => {
    const name = misfit.map ? named.get(misfit.map.id) : undefined
    const fields = Array.isArray(misfit.facts.fields)
      ? misfit.facts.fields.filter((f): f is string => typeof f === 'string')
      : []
    const sheet = typeof misfit.facts.sheet === 'string' ? misfit.facts.sheet : ''
    if (!misfit.map || !name || fields.length === 0) return []
    const listed = fields.map(fieldWords).join(', ')
    if (misfit.problem === 'names_more') {
      return [{ id: misfit.map.id, name, reason: `the sheet '${sheet}' now also names ${listed}, which the map does not read` }]
    }
    if (misfit.problem === 'says_more') {
      return [{
        id: misfit.map.id,
        name,
        reason: `the headings on the sheet '${sheet}' also name ${listed}, which the map does not read, so it may be a different register`,
      }]
    }
    return []
  })
}

/**
 * A rate column the reader cannot use, bound as the GST rate by the suggestion (UAT, 2026-09-30:
 * a 0.18 column bound by hand held every taxed row of a month) — ProDoc's proof's `rate_trap`.
 */
export function rateTrapOf(mapping: MappingNeeded): { column: string; kind: 'fraction' | 'zero' } | null {
  const trap = mapping.unproven_details?.rate_trap
  if (!trap) return null
  const header = typeof trap.header === 'string' && trap.header ? trap.header : null
  const letter = typeof trap.column === 'string' && trap.column ? trap.column : null
  const column = header && letter ? `${header} (column ${letter})` : (header ?? (letter ? `column ${letter}` : 'its column'))
  return { column, kind: trap.kind === 'zero' ? 'zero' : 'fraction' }
}

/** The suggested bindings to a column that holds a value on few rows — the proof's `thin:<field>`. */
export function thinOf(mapping: MappingNeeded): { field: string; column: string | null; percent: number }[] {
  return Object.entries(mapping.unproven_details ?? {}).flatMap(([code, facts]) => {
    if (!code.startsWith('thin:') || typeof facts.percent !== 'number') return []
    const field = code.slice('thin:'.length)
    return [{ field, column: columnWords(mapping.suggestion?.columns[field]), percent: facts.percent }]
  })
}

/** One sheet of a workbook, as ProDoc's survey found it and the upload keeps it (D-83, X-14 (f)). */
export interface SheetRole {
  sheet: string
  words: string
  because: string[]
}

/** The survey's sheets with their role in words — the row's own survey, or its workbook's. */
export function sheetRoles(normalized: Record<string, unknown> | null | undefined): SheetRole[] {
  const survey = (normalized?.survey as { sheets?: unknown } | undefined)?.sheets
  if (!Array.isArray(survey)) return []
  return survey.flatMap((entry): SheetRole[] => {
    const s = entry as { sheet?: unknown; words?: unknown; because?: unknown }
    if (typeof s?.sheet !== 'string' || typeof s.words !== 'string' || !s.words) return []
    const because = Array.isArray(s.because) ? s.because.filter((b): b is string => typeof b === 'string') : []
    return [{ sheet: s.sheet, words: s.words, because }]
  })
}

/**
 * N15: a new map's name, as the old binding dialog named a new layout — the sheet it reads, then
 * `layout` (`GSTR-3B_Working_July_2026 layout` was a binding saved under the first sheet's name,
 * which is why the sheet the map reads is the one named). A CSV's one sheet was `CSV` to the old
 * reader; ProDoc's is unnamed, so it keeps that word.
 */
export function defaultMapName(at: Pick<DocumentMapping, 'sheet'>, mapping: MappingNeeded): string {
  const sheet = at.sheet ?? mapping.suggestion?.sheet ?? mapping.sheets[0]?.name ?? ''
  return `${sheet || 'CSV'} layout`
}

/**
 * D-80, D-148: the buyer GSTINs the preview's rows say are SEZ units — the invoice-type cell reads
 * an SEZ type (SEWP, SEWOP) — and the client's list does not hold yet. What the old dialog offered
 * from the rows it showed; Raw Data offers it on every row once the file is read. Only the rows on
 * the screen (`onPreview`): nothing is kept, sent or logged beyond this component.
 */
export function sezBuyersFromPreview(
  rows: readonly Record<string, unknown>[],
  known: ReadonlySet<string>,
): string[] {
  const found = new Set<string>()
  for (const row of rows) {
    const type = invoiceTypeOf(row.invoice_type)
    if (type !== 'SEWP' && type !== 'SEWOP') continue
    const gstin = String(row.gstin_buyer ?? '').trim().toUpperCase()
    if (/^[0-9]{2}[A-Z0-9]{13}$/.test(gstin) && !known.has(gstin)) found.add(gstin)
  }
  return [...found].sort()
}

/** Why a held register was not read with no person (D-92), as the held row keeps it. */
export function proofReasons(normalized: Record<string, unknown> | null | undefined): string[] {
  const proof = normalized?.proof as { proven?: boolean; reasons?: unknown } | undefined
  if (!proof || proof.proven || !Array.isArray(proof.reasons)) return []
  return proof.reasons.filter((r): r is string => typeof r === 'string' && r.length > 0)
}

/**
 * A refusal in words. ProDoc not answering is said as that — never as the file being wrong — and
 * any other refusal keeps the service's own message, which names what to change.
 */
export function mapperError(err: unknown, otherwise: string): string {
  const e = (err ?? {}) as { code?: unknown; status?: unknown; message?: unknown }
  if (e.code === 'APP_UNAVAILABLE' || e.status === 503) {
    return 'ProDoc, which reads and maps this file, is unavailable just now. Try again shortly.'
  }
  if (err instanceof ApiError || typeof e.code === 'string') {
    return typeof e.message === 'string' && e.message ? e.message : otherwise
  }
  return otherwise
}

interface Opened {
  at: DocumentMapping
  profile: ParseProfile
  mapping: MappingNeeded
  existing: ColumnMap | null
  /** The client's maps as ProGST keeps their meaning — for *Use* and for naming a misfit. */
  maps: ColumnMapRecord[]
  /** The held row as the service keeps it (`normalized`): its proof, and its survey or its workbook's. */
  held: DocumentRecord | null
  roles: SheetRole[]
}

/** The row, and — when it is one sheet of a workbook — the workbook that keeps the survey. */
async function heldRow(documentId: string): Promise<{ held: DocumentRecord | null; roles: SheetRole[] }> {
  const held = await documents.get(documentId).then((r) => r.data, () => null)
  const own = sheetRoles(held?.normalized)
  if (own.length > 0 || !held?.parent_id) return { held, roles: own }
  const parent = await documents.get(held.parent_id).then((r) => r.data, () => null)
  return { held, roles: sheetRoles(parent?.normalized) }
}

export function ColumnMapper({
  documentId,
  clientId,
  filename,
  proof = [],
  onClose,
  onApplied,
}: {
  documentId: string
  clientId: string
  filename: string
  /** Why the file was not read with no person, from its held row (D-92). Omitted, the row's own. */
  proof?: string[]
  onClose: () => void
  /** With the reading the file now has — a new document (ADR-0012). */
  onApplied: (read?: DocumentRecord) => void
}) {
  // The org is read on every call, so a switch mid-dialog is honoured as `api()` honours it.
  const client = useMemo(
    () => createProDocClient({ baseUrl: mapperBase(clientId), fetcher: session.authed, orgId: getOrgId }),
    [clientId],
  )
  const [opened, setOpened] = useState<Opened | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** What is being read: the file again after a save, or under one saved map (its name). */
  const [busy, setBusy] = useState<{ map: string | null } | null>(null)
  /** D-148: the last preview's rows — on this screen only, never sent or kept anywhere. */
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[]>([])
  /** The client's SEZ buyers, so only those it lacks are offered; without the list, none are. */
  const [sezKnown, setSezKnown] = useState<Set<string> | null>(null)
  const [sezError, setSezError] = useState<string | null>(null)
  const [remembering, setRemembering] = useState<string | null>(null)
  const { can } = useEvaluation()

  useEffect(() => {
    let live = true
    void exportFacts
      .get(clientId)
      .then((got) => {
        if (live) setSezKnown(new Set(got.data.sez_buyers.map((b) => b.gstin.toUpperCase())))
      })
      // Without the list nothing is offered to remember; mapping is unaffected.
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [clientId])

  const rememberSez = async (gstin: string) => {
    setRemembering(gstin)
    setSezError(null)
    try {
      // The action Raw Data takes once the file is read: on a person's word.
      const got = await exportFacts.confirmSez(clientId, { gstin, derived_from: 'person' })
      setSezKnown(new Set(got.data.sez_buyers.map((b) => b.gstin.toUpperCase())))
    } catch (err) {
      setSezError(mapperError(err, 'That buyer could not be remembered as SEZ.'))
    } finally {
      setRemembering(null)
    }
  }

  useEffect(() => {
    let live = true
    setOpened(null)
    setError(null)
    void (async () => {
      try {
        // The maps and the held row only add to the mapper: without them it still opens.
        const [at, maps, row] = await Promise.all([
          columnMaps.forDocument(documentId).then((r) => r.data),
          columnMaps.list(clientId).then((r) => r.data.maps, () => [] as ColumnMapRecord[]),
          heldRow(documentId),
        ])
        const [listed, existing] = await Promise.all([
          client.parsing.profiles(),
          at.existing_map_id ? client.parsing.columnMaps.get(at.existing_map_id) : Promise.resolve(null),
        ])
        const profile = listed.items.find((p) => p.name === at.profile)
        if (!profile) {
          throw new ApiError('CONFLICT', 'ProDoc does not offer the columns of this file to map here.', 409)
        }
        if (live) {
          setOpened({
            at, profile, mapping: mappingOf(at.mapping) ?? NOTHING, existing,
            maps: Array.isArray(maps) ? maps : [], held: row.held, roles: row.roles,
          })
        }
      } catch (err) {
        if (live) setError(mapperError(err, 'That file could not be opened to map.'))
      }
    })()
    return () => {
      live = false
    }
  }, [documentId, clientId, client])

  const read = async (map: ColumnMapRecord | null) => {
    setBusy({ map: map?.name ?? null })
    setError(null)
    try {
      onApplied((await (map ? columnMaps.applyMap(documentId, map.id) : columnMaps.readAgain(documentId))).data)
    } catch (err) {
      // After a save the map is kept either way; a refusal is ProDoc reading the file under it and
      // declining. Under a saved map it is why that map does not fit. Either message says what to
      // change, and the mapper stays open on the same file.
      setError(mapperError(err, map ? `The “${map.name}” map did not read this file.` : 'The map was saved, but the file was not read again.'))
    } finally {
      setBusy(null)
    }
  }

  if (!opened) {
    return (
      <Card className="mb-4 p-4">
        {error ? <Alert tone="error">{error}</Alert> : <p className="gst-meta">Opening the file…</p>}
        <div className="mt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </Card>
    )
  }

  const { at, profile, mapping, existing, maps, held, roles } = opened
  const purchase = at.profile === 'purchase_register'
  const reasons = proof.length > 0 ? proof : proofReasons(held?.normalized)
  // The map that read this file is the form itself, not a button beside it.
  const others = maps.filter((m) => m.profile === at.profile && m.id !== existing?.id)
  const refused = existing ? [] : notReused(mapping, maps)
  const thin = thinOf(mapping)
  const rateTrap = rateTrapOf(mapping)
  const sezToRemember = sezKnown ? sezBuyersFromPreview(previewRows, sezKnown) : []

  return (
    <Card className="mb-4 p-4">
      <p className="gst-panel-h mb-1">
        {existing ? 'Change the columns' : 'Map the columns'} — {filename}
      </p>
      {existing ? (
        <p className="gst-meta mb-3">
          This file was read through the <strong>&ldquo;{existing.name}&rdquo;</strong> map,
          shown below as it was saved. Change what is wrong and save: the map is corrected for
          this client, so later files of this shape are read the new way, and this file is read
          again through it. Edits made on its rows stay wherever the invoice number, date, GSTIN
          and taxable value read the same. Its verification does not — look at the file again and
          verify it.
        </p>
      ) : (
        <p className="gst-meta mb-3">
          ProDoc reads this register, and no map this client has fits it yet
          {purchase ? ' — read as a purchase register, so the counterparty is the supplier' : ''}
          . What ProDoc suggests is filled in below; change anything that is wrong, look at the
          preview, and save. The map is kept for this client and used on later files of the same
          shape.
        </p>
      )}

      {refused.length > 0 && (
        <div className="mb-3" data-not-reused="">
          <p className="gst-meta mb-1">A saved map was not used on this file, because:</p>
          <ul className="gst-meta list-disc pl-5">
            {refused.map((map) => (
              <li key={map.id}>
                <strong>{map.name}</strong>: {map.reason}. Map the new columns once, and the map
                saved now is used on later files.
              </li>
            ))}
          </ul>
        </div>
      )}

      {!existing && reasons.length > 0 && (
        <div className="mb-3">
          <p className="gst-meta mb-1">Not read without a person, because:</p>
          <ul className="gst-meta list-disc pl-5">
            {reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <Alert tone="error" className="mb-3">
          {error}
        </Alert>
      )}
      {busy && (
        <p className="gst-meta mb-3">
          {busy.map ? `Reading the file through “${busy.map}”…` : 'Reading the file again…'}
        </p>
      )}

      {others.length > 0 && (
        <div className="mb-4" data-saved-maps="">
          <p className="gst-meta mb-2">
            {existing ? 'Other maps saved for this client:' : 'Maps already saved for this client:'}
          </p>
          <div className="flex flex-wrap gap-2">
            {others.map((map) => (
              <Button
                key={map.id}
                type="button"
                variant="outline"
                size="sm"
                disabled={busy !== null}
                onClick={() => void read(map)}
              >
                Use &ldquo;{map.name}&rdquo;
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* D-83: what each sheet is, by its structure — which sheet is the register shows before
          anyone picks a sheet in the mapper. */}
      {roles.length > 1 && (
        <div className="mb-3" data-sheet-roles="">
          <p className="gst-meta mb-1">What each sheet is:</p>
          <ul className="gst-meta list-disc pl-5">
            {roles.map((s) => (
              <li key={s.sheet} title={s.because.join('; ') || s.words}>
                <span className="gst-mono">{s.sheet}</span>
                {` · ${s.words}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {thin.length > 0 && (
        <Alert tone="warning" className="mb-3">
          {thin.map(({ field, column, percent }) => (
            <p key={field}>
              As suggested, <strong>{FIELD_LABELS[field] ?? field}</strong> is bound to{' '}
              <strong>{column ?? 'its column'}</strong>, which holds a value on only{' '}
              <strong>{percent}%</strong> of the rows on this sheet. A register carries one on
              every row, so this is usually the wrong column — check whether another sheet or
              column holds it before saving.
            </p>
          ))}
        </Alert>
      )}

      {rateTrap && (
        <Alert tone="warning" className="mb-3">
          <p>
            As suggested, <strong>GST rate</strong> is bound to <strong>{rateTrap.column}</strong>,{' '}
            {rateTrap.kind === 'fraction'
              ? 'which writes its rates as fractions — 0.18 for 18%. ProGST reads a rate as a number of percent, so 0.18 is read as 0.18%'
              : 'which is 0 on every row'}
            , and every taxed row will be held on the tax check.{' '}
            {purchase
              ? 'Leave GST rate as “Not used”: the reconciliation and the credit are worked from the tax columns.'
              : 'Bind a column that holds the rate as a percentage — 18, 5 — instead.'}
          </p>
        </Alert>
      )}

      {sezToRemember.length > 0 && can(CLIENT, 'can_update') && (
        <div className="mb-3" data-sez-offer="">
          <p className="gst-meta mb-2">
            The invoice type on the rows shown says these buyers are SEZ units. Remembered
            on the client&rsquo;s list, their invoices file as SEZ supplies (Table 6B) in
            every month not yet filed:
          </p>
          {sezError && (
            <Alert tone="error" className="mb-2">
              {sezError}
            </Alert>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {sezToRemember.map((gstin) => (
              <span key={gstin} className="inline-flex items-center gap-1">
                <span className="gst-mono">{gstin}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={remembering !== null}
                  title="Adds the buyer to the client's list of SEZ buyers, on your own word"
                  onClick={() => void rememberSez(gstin)}
                >
                  Remember this buyer as SEZ
                </Button>
              </span>
            ))}
          </div>
        </div>
      )}

      <ProDocColumnMapper
        client={client}
        documentId={at.document_id}
        versionId={at.version_id}
        profile={profile}
        mapping={mapping}
        scope={at.scope}
        existing={existing}
        defaultName={defaultMapName(at, mapping)}
        fieldLabels={FIELD_LABELS}
        onPreview={(preview: MapPreview) => setPreviewRows(preview.rows)}
        onSaved={() => void read(null)}
        onCancel={onClose}
      />
    </Card>
  )
}
